"use client";

import { useState, useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useFetchAccessPoint } from "../../lib/util";
import { useClient } from "../../lib/context/auth-context";
import { useHistoryManager } from "../history/useHistoryManager";
import {
  setOrganisation,
  setAccessPoint,
  setAccessPointId,
  setAccessPoints,
} from "../slice/accessOrg-slice";
import { useAppDispatch } from "../slice/hooks";

export const useMarketplaceOrgs = (
  initialUrlData = {},
  token,

  limit = 10
) => {
  const client = useClient();
  const dispatch = useAppDispatch();
  const { navigateTo } = useHistoryManager();

  const [orgState, setOrgState] = useState({
    organisations: [],
    limit: limit,
    total: 0,
    currentPage: 1,
    page: 1,
    search: "",
    ...initialUrlData,
  });

  const [switching, setSwitching] = useState(false);
  const [newOrganisation, setNewOrganisation] = useState(null);

  // --------------------------
  //  FETCH ORGANISATIONS
  // --------------------------
  const orgQueryKey = useMemo(
    () => ["feeds", orgState.limit, orgState.page, orgState.search],
    [orgState.limit, orgState.page, orgState.search]
  );

  const {
    data: feeds,
    isLoading,
    isFetching,
    error,
  } = useQuery(
    orgQueryKey,
    async () => {
      const params = [
        `limit=${orgState.limit}`,
        `page=${orgState.page}`,
        `searchTerm=${orgState.search}`,
      ];
      const res = await client(
        `organisation/organisations/storefront/enabled?${params.join("&")}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return res.data;
    },
    {
      enabled: Boolean(token),
      keepPreviousData: true,
      onError: (err) => {
        console.error("Failed to fetch organisations:", err);
        setSwitching(false);
      },
    }
  );

  useEffect(() => {
    if (feeds && feeds.organisations) {
      const { organisations, limit, total, currentPage } = feeds;

      setOrgState((prev) => {
        const all = [...(prev.organisations || []), ...organisations];
        const unique = all.filter(
          (org, index, self) =>
            index === self.findIndex((o) => o.orgId === org.orgId)
        );
        return { ...prev, organisations: unique, limit, total, currentPage };
      });
    }
  }, [feeds]);

  const openPage = (page) => {
    setOrgState((prev) => ({ ...prev, page }));
  };

  // --------------------------
  //  FETCH ACCESS POINT + SWITCH
  // --------------------------
  const { mutateAsync: fetchAccessPoint } = useFetchAccessPoint({
    onError: (error) => {
      console.error("Failed to fetch access point:", error);
      setSwitching(false);
    },
    onSuccess: (response) => {
      const accessPoints = response.data.accessPoints;
      navigateTo(
        `${`store/${newOrganisation.handle}`} `,
        {
          onNavigate: (navigate) => {
            setSwitching(false);
            if (navigate) {
              if (accessPoints && accessPoints.length > 0) {
                dispatch(setAccessPointId(accessPoints[0].accessPointId));
                dispatch(setAccessPoint(accessPoints[0]));
                dispatch(setAccessPoints(accessPoints));
              } else {
                console.log(
                  "No access points found for:",
                  newOrganisation.orgName
                );
              }
              if (typeof window !== "undefined") {
                window.scrollTo({
                  top: 0,
                  behavior: "smooth",
                });
              }
            }
          },
        },
        false
      );
    },
  });

  const handleSwitch = async (org) => {
    setNewOrganisation(org);
    dispatch(setOrganisation(org));
    setSwitching(true);
    fetchAccessPoint({
      orgId: org?.orgId,
      token,
    });
  };

  return {
    orgState,
    openPage,
    handleSwitch,
    switching,
    isLoading,
    isFetching,
    error,
  };
};
